import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { API_BASE } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const inputClass =
  'w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500';

const Register = () => {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [accountType, setAccountType] = useState('residential');
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    company_name: '',
    message: '',
  });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/customer/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          account_type: accountType,
          ...form,
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        if (data.code === 'EMAIL_EXISTS') {
          toast.error(
            (t) => (
              <span>
                {data.message}{' '}
                <Link to="/login" className="underline font-medium" onClick={() => toast.dismiss(t.id)}>
                  Sign in
                </Link>
              </span>
            ),
            { duration: 6000 }
          );
        } else {
          toast.error(data.message || 'Registration failed');
        }
        return;
      }

      if (data.pendingApproval) {
        toast.success(data.message);
        navigate('/registration-pending');
        return;
      }

      setSession(data.data.token, data.data.customer);
      toast.success('Account created');
      navigate('/dashboard');
    } catch {
      toast.error('Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-md w-full max-w-lg p-8">
        <h1 className="text-2xl font-semibold text-teal-800">Create account</h1>
        <p className="text-slate-600 text-sm mt-1 mb-6">
          Choose how you&apos;ll use our cleaning service.
        </p>

        <div className="grid grid-cols-2 gap-3 mb-6">
          <button
            type="button"
            onClick={() => setAccountType('residential')}
            className={`p-4 rounded-lg border-2 text-left transition ${
              accountType === 'residential'
                ? 'border-teal-600 bg-teal-50'
                : 'border-slate-200 hover:border-slate-300'
            }`}
          >
            <span className="font-medium block">Residential</span>
            <span className="text-xs text-slate-600 mt-1">Home cleaning — start right away</span>
          </button>
          <button
            type="button"
            onClick={() => setAccountType('commercial')}
            className={`p-4 rounded-lg border-2 text-left transition ${
              accountType === 'commercial'
                ? 'border-teal-600 bg-teal-50'
                : 'border-slate-200 hover:border-slate-300'
            }`}
          >
            <span className="font-medium block">Commercial</span>
            <span className="text-xs text-slate-600 mt-1">Business — we&apos;ll follow up to activate</span>
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {accountType === 'commercial' && (
            <div>
              <label className="block text-sm font-medium mb-1">Company name *</label>
              <input
                className={inputClass}
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                required
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">Your name *</label>
            <input
              className={inputClass}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Email *</label>
            <input
              type="email"
              className={inputClass}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Password *</label>
            <input
              type="password"
              className={inputClass}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              minLength={8}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Phone</label>
            <input
              className={inputClass}
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </div>
          {accountType === 'commercial' && (
            <div>
              <label className="block text-sm font-medium mb-1">Tell us about your needs</label>
              <textarea
                className={inputClass}
                rows={3}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Properties, frequency, special requirements…"
              />
            </div>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-teal-700 text-white py-2.5 rounded-lg hover:bg-teal-800 disabled:opacity-50"
          >
            {submitting ? 'Creating account…' : 'Create account'}
          </button>
        </form>

        <p className="text-sm text-slate-600 mt-6 text-center">
          Already have an account?{' '}
          <Link to="/login" className="text-teal-700 font-medium hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
